import { useState, useEffect, useCallback } from "react";
import { notificationStore } from "../utils/notificationStore";
import { useAdminData } from "../AdminDataContext";
import { apiGet, apiPost } from "../../../api/http";
import { showAlert } from "../../../api/errors";

type RenewalStatus = "pending" | "approved" | "rejected";

interface RenewalApplication {
  id: string;
  memberName: string;
  memberNo: string;
  phone: string;
  level: string;
  levelClass: string;
  currentExpire: string;
  newExpire: string;
  submittedAt: string;
  annualFee: number;
  paymentProofUrl: string;
  status: RenewalStatus;
}

const STATUS_CONFIG: Record<RenewalStatus, { label: string; dot: string; badge: string }> = {
  pending: { label: "待审核", dot: "bg-amber-400", badge: "bg-amber-50 text-amber-700" },
  approved: { label: "已通过", dot: "bg-green-400", badge: "bg-green-50 text-green-700" },
  rejected: { label: "已拒绝", dot: "bg-red-400", badge: "bg-red-50 text-red-600" },
};

export default function RenewalManager() {
  const { tenantLegacyId } = useAdminData();
  const [renewals, setRenewals] = useState<RenewalApplication[]>([]);
  const [filter, setFilter] = useState<"all" | RenewalStatus>("all");
  const [proofViewing, setProofViewing] = useState<RenewalApplication | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    if (!tenantLegacyId) return;
    const rows = await apiGet<RenewalApplication[]>(`/api/tenants/${tenantLegacyId}/renewal-queue`);
    setRenewals(rows);
  }, [tenantLegacyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const displayed = filter === "all" ? renewals : renewals.filter((r) => r.status === filter);
  const pendingCount = renewals.filter((r) => r.status === "pending").length;

  const handleApprove = async (id: string) => {
    if (!tenantLegacyId) return;
    const renewal = renewals.find((r) => r.id === id);
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/renewal-queue/${encodeURIComponent(id)}/approve`, {});
      if (renewal) {
        await notificationStore.sendRenewalApprovalNotif(
          tenantLegacyId,
          renewal.memberName,
          renewal.level,
          renewal.newExpire,
          renewal.annualFee,
        );
      }
      await load();
    } catch {
      showAlert("操作失败，请稍后再试");
    }
  };

  const handleReject = async (id: string) => {
    if (!tenantLegacyId) return;
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/renewal-queue/${encodeURIComponent(id)}/reject`, {
        reason: rejectReason.trim() || undefined,
      });
      setRejectingId(null);
      setRejectReason("");
      await load();
    } catch {
      showAlert("操作失败，请稍后再试");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900">续费申请管理</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            待审核 <strong className="text-amber-600">{pendingCount}</strong> 条续费申请
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full"></span>待审核</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-400 rounded-full"></span>已通过</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-full"></span>已拒绝</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="px-6 pt-3 pb-2 border-b border-gray-50 flex items-center gap-2">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(["all", "pending", "approved", "rejected"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${filter === s ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s === "all" ? "全部" : STATUS_CONFIG[s].label}
              <span className="ml-1 text-gray-400">
                {s === "all" ? renewals.length : renewals.filter((r) => r.status === s).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {["会员信息", "当前等级", "当前到期", "续费后到期", "年费", "提交时间", "状态", "操作"].map((h) => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {displayed.map((r) => {
              const st = STATUS_CONFIG[r.status];
              return (
                <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-red-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{r.memberName}</p>
                        <p className="text-xs text-gray-400 font-mono">{r.memberNo}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${r.levelClass}`}>{r.level}</span>
                  </td>
                  <td className="px-5 py-4 text-gray-600 text-xs whitespace-nowrap">{r.currentExpire}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold whitespace-nowrap ${r.status === "approved" ? "text-green-600" : "text-gray-500"}`}>
                      {r.newExpire}
                    </span>
                    {r.status === "approved" && (
                      <span className="ml-1.5 text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full">已延续</span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-bold text-red-600 text-sm whitespace-nowrap">¥{r.annualFee}</td>
                  <td className="px-5 py-4 text-gray-500 text-xs whitespace-nowrap">{r.submittedAt}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.badge}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setProofViewing(r)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg text-xs cursor-pointer transition-colors whitespace-nowrap border border-gray-200"
                      >
                        <i className="ri-image-line"></i>凭证
                      </button>
                      {r.status === "pending" && (
                        <>
                          <button
                            onClick={() => void handleApprove(r.id)}
                            className="px-2.5 py-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
                          >
                            通过
                          </button>
                          <button
                            onClick={() => setRejectingId(r.id)}
                            className="px-2.5 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
                          >
                            拒绝
                          </button>
                        </>
                      )}
                      {r.status !== "pending" && (
                        <span className="text-xs text-gray-400">已处理</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayed.length === 0 && (
          <div className="py-16 text-center">
            <i className="ri-file-list-3-line text-4xl text-gray-200 block mb-3"></i>
            <p className="text-sm text-gray-400">暂无续费申请记录</p>
          </div>
        )}
      </div>

      {/* Payment Proof Modal */}
      {proofViewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setProofViewing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">缴费凭证</h3>
                <p className="text-xs text-gray-400 mt-0.5">{proofViewing.memberName} · {proofViewing.memberNo}</p>
              </div>
              <button onClick={() => setProofViewing(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-sm"></i>
              </button>
            </div>
            <div className="p-5 space-y-3">
              {/* Info summary */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "会员等级", value: proofViewing.level },
                  { label: "续费金额", value: `¥${proofViewing.annualFee}` },
                  { label: "当前到期", value: proofViewing.currentExpire },
                  { label: "续费后到期", value: proofViewing.newExpire },
                ].map((row) => (
                  <div key={row.label} className="bg-gray-50 rounded-xl px-3 py-2">
                    <p className="text-[10px] text-gray-400 mb-0.5">{row.label}</p>
                    <p className="text-xs font-semibold text-gray-800">{row.value}</p>
                  </div>
                ))}
              </div>
              {/* Proof image */}
              <div className="rounded-2xl overflow-hidden border border-gray-100 bg-gray-50" style={{ maxHeight: 320 }}>
                <img
                  src={proofViewing.paymentProofUrl}
                  alt="缴费凭证"
                  className="w-full object-cover object-top"
                  style={{ maxHeight: 320 }}
                />
              </div>
            </div>
            {proofViewing.status === "pending" && (
              <div className="flex gap-3 px-5 pb-5">
                <button
                  onClick={() => { setRejectingId(proofViewing.id); setProofViewing(null); }}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:border-red-200 hover:text-red-600 transition-colors whitespace-nowrap"
                >
                  拒绝申请
                </button>
                <button
                  onClick={() => { void handleApprove(proofViewing.id); setProofViewing(null); }}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  确认通过 · 延续至 {proofViewing.newExpire}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h3 className="font-bold text-gray-900 mb-1">拒绝续费申请</h3>
            <p className="text-xs text-gray-400 mb-4">请填写拒绝原因，将通知给申请人</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="如：缴费凭证不清晰，请重新上传..."
              rows={3}
              maxLength={200}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none mb-4"
            ></textarea>
            <div className="flex gap-3">
              <button onClick={() => setRejectingId(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                取消
              </button>
              <button
                onClick={() => void handleReject(rejectingId)}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-red-700 whitespace-nowrap"
              >
                确认拒绝
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
